const API_ROOT = process.env.REACT_APP_API_URL || "http://localhost:8001";
const BASE_URL = `${API_ROOT}/dashboard`;
const BOT_URL = `${API_ROOT}/bot`;

let token = localStorage.getItem("zero_token");

const setToken = (t) => {
  token = t;
  if (t) localStorage.setItem("zero_token", t);
  else localStorage.removeItem("zero_token");
};

// SEC-13: el token y los datos de usuario viven en localStorage sin cifrar.
// "Cifrarlos" client-side no protege de nada real -- la clave de descifrado
// estaría en el mismo JS que un XSS ya puede leer, así que sería teatro y no
// una mitigación. Lo que sí reduce la ventana de exposición de una sesión
// vieja es no confiar en ella pasado su expires_at, en vez de esperar a que
// el próximo request pegue un 401 para recién ahí limpiarla.
const loadStoredUser = () => {
  const t = localStorage.getItem("zero_token");
  const raw = localStorage.getItem("zero_user");
  if (!t || !raw) return null;
  let saved;
  try {
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (saved.expires_at && new Date(saved.expires_at).getTime() <= Date.now()) {
    setToken(null);
    localStorage.removeItem("zero_user");
    return null;
  }
  return saved;
};

const request = async (method, path, body = null, base = BASE_URL, signal = undefined) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    signal,
  });

  // Un 401 en /login son credenciales invalidas, no una sesion vencida -- hay
  // que dejar que caiga al !res.ok de abajo para que login() reciba el error
  // real en vez de que la pagina se recargue en silencio (BUG-6).
  if (res.status === 401 && path !== "/login") {
    setToken(null);
    window.location.reload();
    return;
  }

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || "Error desconocido");
  }

  return res.json();
};

export const api = {
  // Auth
  login: (email, password) => {
    return request("POST", "/login", { email, password }).then((data) => {
      setToken(data.token);
      localStorage.setItem("zero_user", JSON.stringify(data));
      return data;
    });
  },
  logout: () => {
    return request("POST", "/logout").finally(() => {
      setToken(null);
      localStorage.removeItem("zero_user");
    });
  },
  getToken: () => token,
  loadStoredUser,

  // Soporte
  getStatus: (signal) => request("GET", "/support/status", null, BASE_URL, signal),
  getTransactions: (status, flagged, signal) => {
    const q = new URLSearchParams();
    if (status && status !== "all") q.set("status", status);
    if (flagged) q.set("flagged", "true");
    const qs = q.toString();
    return request("GET", `/support/transactions${qs ? `?${qs}` : ""}`, null, BASE_URL, signal);
  },
  getTransactionEvents: (transactionId) =>
    request("GET", `/support/transactions/${transactionId}/events`),
  getParams: (signal) => request("GET", "/support/params", null, BASE_URL, signal),
  updateParam: (key, value) => request("PUT", `/support/params/${key}`, { value }),
  getAuditLog: (signal) => request("GET", "/support/audit", null, BASE_URL, signal),
  getSupportFees: (status, signal) => request("GET", `/support/fees?status=${status}&limit=50`, null, BASE_URL, signal),
  getMaintenance: () => request("GET", "/support/maintenance"),
  setMaintenance: (active, message, allowedPhones) =>
    request("PUT", "/support/maintenance", { active, message, allowed_phones: allowedPhones }),

  // Negocio
  getStats: (period) => request("GET", `/business/stats?period=${period}`),
  getMonthly: (signal) => request("GET", "/business/monthly", null, BASE_URL, signal),
  getFees: (period, signal) => request("GET", `/business/fees?period=${period}`, null, BASE_URL, signal),
  getLiveTransactions: (since, signal) =>
    request("GET", `/business/live-transactions${since ? `?since=${encodeURIComponent(since)}` : ""}`, null, BASE_URL, signal),

  // Waitlist (PERF-4: paginado por limit/offset en vez de traer la tabla entera)
  getWaitlist: (offset = 0, limit = 50) => request("GET", `/waitlist?limit=${limit}&offset=${offset}`),
  getWaitlistNotifyPreview: () => request("GET", "/waitlist/notify-preview"),
  notifyWaitlist: () => request("POST", "/waitlist/notify"),

  // Usuarios
  getUsers: (search) => request("GET", `/users${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  toggleUserActive: (userId, isActive) => request("PATCH", `/users/${userId}/active`, { is_active: isActive }),
  setProviderStatus: (code, status) => request("PATCH", `/providers/${code}/status`, { status }),
  resetUser: (phone) => request("POST", "/reset-user", { phone }, `${API_ROOT}/demo`),

  // Simulador (DEBT-10: antes Simulator.js pegaba fetch directo con su propia
  // copia de la lógica de auth, sin el manejo de 401/sesión vencida de request())
  getBotSession: (phone) => request("GET", `/session/${encodeURIComponent(phone)}`, null, BOT_URL),
  simulateBotMessage: (phone, message, interactivePayload) => {
    const body = interactivePayload ? { phone, message, interactive_payload: interactivePayload } : { phone, message };
    return request("POST", "/simulate", body, BOT_URL);
  },
  resetBotDemo: () => request("POST", "/demo/reset", null, BOT_URL),
};